import { useEffect, useState } from 'react';
import {
  getMemberNumber,
  assignMemberNumber,
  type MemberNumberResponse,
} from '@urbanik/api-client';
import {
  Button,
  CanonicalCard,
  ConfirmationDialog,
  FormSection,
  FormActions,
  InputField,
  SearchSelectField,
  TextAreaField,
  InfoPanel,
  SummaryList,
  SummaryListItem,
  formatDateTime,
} from '@urbanik/ui';
import { Hash, Save, RefreshCw } from 'lucide-react';
import { apiBase } from './api-base';

export function MemberNumberEditor({ memberId }: { memberId: string }) {
  const [data, setData] = useState<MemberNumberResponse | null>(null);
  const [number, setNumber] = useState('');
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const c = new AbortController();
    setData(null);
    setProblem('');
    setNotice('');
    setNumber('');
    setReason('');
    setReview(false);
    getMemberNumber(apiBase, memberId, c.signal)
      .then(setData)
      .catch((error) => {
        if (!c.signal.aborted)
          setProblem(error instanceof Error ? error.message : 'No pudimos consultar el número.');
      });
    return () => c.abort();
  }, [memberId, attempt]);
  const current = data?.history.find((h) => h.endedAt === null);
  const save = async () => {
    if (!data || busy) return;
    setBusy(true);
    setProblem('');
    setNotice('');
    try {
      setData(
        await assignMemberNumber(apiBase, memberId, {
          number,
          reason,
          expectedAssignmentId: current?.id ?? null,
        }),
      );
      setNumber('');
      setReason('');
      setNotice('Número asignado. El historial anterior se ha conservado.');
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'No pudimos asignar el número.');
    } finally {
      setBusy(false);
      setReview(false);
    }
  };
  return (
    <CanonicalCard title="Número de socio" icon={<Hash aria-hidden="true" />}>
      {problem ? (
        <InfoPanel tone="danger" title="Revisa la asignación">
          {problem}
        </InfoPanel>
      ) : null}
      {notice ? <InfoPanel tone="success">{notice}</InfoPanel> : null}
      {!data ? (
        <InfoPanel title={problem ? 'Numeración no disponible' : 'Consultando numeración…'}>
          {problem ? (
            <Button
              onClick={() => setAttempt((v) => v + 1)}
              icon={<RefreshCw aria-hidden="true" />}
            >
              Reintentar
            </Button>
          ) : null}
        </InfoPanel>
      ) : (
        <>
          <SummaryList>
            <SummaryListItem
              label="Número vigente"
              value={
                current?.number ??
                (data.history.length ? 'Sin asignación vigente' : 'Sin número registrado')
              }
            />
          </SummaryList>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setReview(true);
            }}
          >
            <FormSection
              title="Asignación administrativa"
              description="Elige un número libre o escribe uno nuevo. Los números ocupados y retirados no pueden asignarse."
            >
              <SearchSelectField
                id="available-member-number"
                label="Buscar un número disponible"
                hint={`${data.available.length} números libres en la entidad activa.`}
                placeholder="Escribe para buscar y seleccionar"
                emptyMessage="No hay números disponibles que coincidan."
                value={data.available.includes(number) ? number : ''}
                onValueChange={setNumber}
                options={data.available.map((n) => ({
                  value: n,
                  label: `N.º ${n}`,
                  meta: 'Disponible',
                }))}
                disabled={busy}
              />
              <InputField
                label="Número a asignar o crear"
                value={number}
                onChange={(event) => setNumber(event.target.value)}
                inputMode="numeric"
                pattern="[0-9]{1,9}"
                maxLength={9}
                required
                disabled={busy}
              />
              <TextAreaField
                label="Motivo de la asignación"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                minLength={5}
                maxLength={500}
                required
                disabled={busy}
              />
            </FormSection>
            <FormActions>
              <Button
                type="button"
                disabled={busy}
                icon={<RefreshCw aria-hidden="true" />}
                onClick={() => setAttempt((v) => v + 1)}
              >
                Actualizar disponibilidad
              </Button>
              <Button
                type="submit"
                variant="primary"
                icon={<Save aria-hidden="true" />}
                disabled={busy || !number.trim() || reason.trim().length < 5}
              >
                Revisar asignación
              </Button>
            </FormActions>
          </form>
          <SummaryList title="Historial de números">
            {data.history.length ? (
              data.history.map((h) => (
                <SummaryListItem
                  key={h.id}
                  label={`N.º ${h.number} · ${h.endedAt ? 'Histórico' : 'Vigente'}`}
                  value={
                    <>
                      <span>
                        {formatDateTime(h.startedAt)} —{' '}
                        {h.endedAt ? formatDateTime(h.endedAt) : 'Actual'}
                      </span>
                      <p>{h.reason ?? 'Asignación procedente de importación'}</p>
                      {h.actor ? <small>Responsable: {h.actor}</small> : null}
                    </>
                  }
                />
              ))
            ) : (
              <SummaryListItem label="Asignaciones anteriores" value="No registradas" />
            )}
          </SummaryList>
        </>
      )}
      <ConfirmationDialog
        open={review}
        busy={busy}
        title="Confirmar número de socio"
        description={`Se asignará el número ${number}.${current ? ` La asignación ${current.number} se cerrará y quedará en el historial.` : ''} Motivo: ${reason}`}
        onCancel={() => {
          if (!busy) setReview(false);
        }}
        onConfirm={() => void save()}
      />
    </CanonicalCard>
  );
}
